import { useState, useMemo } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Plus, Edit2, Trash2, Search, Tags, CheckCircle2, XCircle,
  ChevronLeft, ChevronRight, ArrowUp, ArrowDown
} from 'lucide-react';
import {
  useFreelancerSkills,
  useCreateFreelancerSkill,
  useUpdateFreelancerSkill,
  useDeleteFreelancerSkill,
  useReorderFreelancerSkills,
} from '@/lib/hooks/use-freelancer-skills';
import { cn } from '@/lib/utils';
import type { Database } from '@/lib/supabase/supabase-types';

type FreelancerSkill = Database['public']['Tables']['freelancer_skills']['Row'];

const PAGE_SIZE = 15;

const SKILL_CATEGORIES = [
  'Core AI',
  'Programming',
  'Frameworks',
  'Data & Analytics',
  'Infrastructure',
  'Operations',
  'Governance',
  'Other',
];

// Stat card component
function StatCard({
  icon: Icon,
  count,
  label,
  iconBgClass = 'bg-[rgba(70,105,237,0.10)]'
}: {
  icon: React.ElementType;
  count: number;
  label: string;
  iconBgClass?: string;
}) {
  return (
    <div className="bg-white rounded-[40px] border border-slate-200 flex-1 h-[214px] flex flex-col items-center justify-center">
      <div className={cn("w-16 h-16 rounded-full flex items-center justify-center mb-4", iconBgClass)}>
        <Icon className="w-6 h-6 text-[#4669ed]" />
      </div>
      <div className="text-[40px] leading-[42px] font-normal text-slate-900 tracking-tight">
        {count}
      </div>
      <div className="text-sm text-slate-500 mt-2">
        {label}
      </div>
    </div>
  );
}

// Status badge component
function ActiveBadge({ isActive }: { isActive: boolean }) {
  if (isActive) {
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-[rgba(16,185,129,0.10)] text-emerald-500">
        <CheckCircle2 className="w-3 h-3" />
        Active
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-500">
      <XCircle className="w-3 h-3" />
      Inactive
    </span>
  );
}

export default function SkillsManagement() {
  const { data: skills = [], isLoading } = useFreelancerSkills(true);
  const createSkill = useCreateFreelancerSkill();
  const updateSkill = useUpdateFreelancerSkill();
  const deleteSkill = useDeleteFreelancerSkill();
  const reorderSkills = useReorderFreelancerSkills();

  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [editingSkill, setEditingSkill] = useState<FreelancerSkill | null>(null);
  const [deleteConfirmSkill, setDeleteConfirmSkill] = useState<FreelancerSkill | null>(null);

  const [newSkillName, setNewSkillName] = useState('');
  const [newSkillCategory, setNewSkillCategory] = useState('');
  const [newSkillActive, setNewSkillActive] = useState(true);

  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [page, setPage] = useState(1);

  const filteredSkills = useMemo(() => {
    return skills.filter(skill => {
      const matchesSearch = skill.name.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = categoryFilter === 'all' || skill.category === categoryFilter;
      return matchesSearch && matchesCategory;
    });
  }, [skills, searchQuery, categoryFilter]);

  // Pagination
  const totalCount = filteredSkills.length;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);
  const paginatedSkills = filteredSkills.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE
  );

  const categories = Array.from(new Set(skills.map(s => s.category).filter(Boolean)));

  // Stats
  const stats = useMemo(() => {
    return {
      total: skills.length,
      active: skills.filter(s => s.is_active).length,
      inactive: skills.filter(s => !s.is_active).length,
      categories: new Set(skills.map(s => s.category).filter(Boolean)).size,
    };
  }, [skills]);

  const handleAddSkill = async () => {
    if (!newSkillName.trim()) return;

    await createSkill.mutateAsync({
      name: newSkillName.trim(),
      category: newSkillCategory || null,
      is_active: newSkillActive,
      display_order: skills.length + 1,
    });

    setIsAddDialogOpen(false);
    setNewSkillName('');
    setNewSkillCategory('');
    setNewSkillActive(true);
  };

  const handleUpdateSkill = async () => {
    if (!editingSkill || !editingSkill.name.trim()) return;

    await updateSkill.mutateAsync({
      id: editingSkill.id,
      name: editingSkill.name.trim(),
      category: editingSkill.category,
      is_active: editingSkill.is_active,
    });

    setEditingSkill(null);
  };

  const handleDeleteSkill = async () => {
    if (!deleteConfirmSkill) return;

    await deleteSkill.mutateAsync(deleteConfirmSkill.id);
    setDeleteConfirmSkill(null);
  };

  const handleToggleActive = async (skill: FreelancerSkill) => {
    await updateSkill.mutateAsync({
      id: skill.id,
      is_active: !skill.is_active,
    });
  };

  const handleMoveUp = async (skill: FreelancerSkill, index: number) => {
    if (index === 0) return;

    const globalIndex = (page - 1) * PAGE_SIZE + index;
    const prevSkill = filteredSkills[globalIndex - 1];
    if (!prevSkill) return;

    const updates = [
      { id: skill.id, display_order: prevSkill.display_order || globalIndex },
      { id: prevSkill.id, display_order: skill.display_order || globalIndex + 1 },
    ];

    await reorderSkills.mutateAsync(updates);
  };

  const handleMoveDown = async (skill: FreelancerSkill, index: number) => {
    const globalIndex = (page - 1) * PAGE_SIZE + index;
    if (globalIndex >= filteredSkills.length - 1) return;

    const nextSkill = filteredSkills[globalIndex + 1];
    if (!nextSkill) return;

    const updates = [
      { id: skill.id, display_order: nextSkill.display_order || globalIndex + 2 },
      { id: nextSkill.id, display_order: skill.display_order || globalIndex + 1 },
    ];

    await reorderSkills.mutateAsync(updates);
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-[40px] border border-slate-200 p-12 text-center">
        <p className="text-slate-500">Loading skills...</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Stat Cards */}
      <div className="flex gap-6">
        <StatCard icon={Tags} count={stats.total} label="Total Skills" />
        <StatCard icon={CheckCircle2} count={stats.active} label="Active" />
        <StatCard icon={XCircle} count={stats.inactive} label="Inactive" />
        <StatCard icon={Tags} count={stats.categories} label="Categories" />
      </div>

      {/* Search & Filter Section */}
      <div className="bg-white rounded-[40px] border border-slate-200 p-6 shadow-sm">
        <div className="flex gap-4 items-center">
          {/* Search Input */}
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search skills..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(1);
              }}
              className="pl-10 rounded-[40px] border-slate-200 h-12"
            />
          </div>

          {/* Category Filter */}
          <Select
            value={categoryFilter}
            onValueChange={(value) => {
              setCategoryFilter(value);
              setPage(1);
            }}
          >
            <SelectTrigger className="w-[200px] rounded-[40px] bg-slate-100 border-slate-200 h-12">
              <SelectValue placeholder="All categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {categories.map((cat) => (
                <SelectItem key={cat} value={cat || 'uncategorized'}>
                  {cat || 'Uncategorized'}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Add Skill Button */}
          <button
            onClick={() => setIsAddDialogOpen(true)}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-white font-bold text-sm"
            style={{
              background: 'linear-gradient(106.66deg, rgba(70, 105, 237, 1) 0%, rgba(54, 84, 233, 1) 100%)',
            }}
          >
            <Plus className="w-5 h-5" />
            Add Skill
          </button>
        </div>
      </div>

      {/* Table Section */}
      <div className="bg-white rounded-[40px] border border-slate-200 overflow-hidden shadow-sm">
        {/* Table Header */}
        <div className="flex items-center justify-between px-6 py-6 border-b border-slate-200">
          <h3 className="text-lg font-bold text-slate-900">
            All Skills ({totalCount})
          </h3>
          <div className="text-sm text-slate-500">
            {stats.active} active, {stats.inactive} inactive
          </div>
        </div>

        {/* Table */}
        {paginatedSkills.length === 0 ? (
          <div className="p-12 text-center text-slate-500">
            {searchQuery || categoryFilter !== 'all'
              ? 'No skills found matching your filters.'
              : 'No skills found. Add your first skill to get started.'}
          </div>
        ) : (
          <>
            {/* Column Headers */}
            <div className="bg-slate-50 border-b border-slate-200">
              <div className="grid grid-cols-[0.5fr_2fr_1.5fr_1fr_1.5fr] px-6 py-4">
                <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">Order</div>
                <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">Name</div>
                <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">Category</div>
                <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">Status</div>
                <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">Actions</div>
              </div>
            </div>

            {/* Table Rows */}
            <div>
              {paginatedSkills.map((skill, index) => (
                <div
                  key={skill.id}
                  className="grid grid-cols-[0.5fr_2fr_1.5fr_1fr_1.5fr] px-6 py-4 border-b border-slate-100 items-center hover:bg-slate-50/50"
                >
                  {/* Order */}
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleMoveUp(skill, index)}
                      disabled={index === 0 && page === 1}
                      className="w-6 h-6 rounded flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 disabled:opacity-30"
                    >
                      <ArrowUp className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleMoveDown(skill, index)}
                      disabled={(page - 1) * PAGE_SIZE + index >= filteredSkills.length - 1}
                      className="w-6 h-6 rounded flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 disabled:opacity-30"
                    >
                      <ArrowDown className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Name */}
                  <div className="font-semibold text-sm text-slate-900">
                    {skill.name}
                  </div>

                  {/* Category */}
                  <div>
                    {skill.category ? (
                      <span className="px-3 py-1 rounded-full bg-slate-100 text-xs font-semibold text-slate-700">
                        {skill.category}
                      </span>
                    ) : (
                      <span className="text-sm text-slate-400">-</span>
                    )}
                  </div>

                  {/* Status */}
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={skill.is_active ?? false}
                      onCheckedChange={() => handleToggleActive(skill)}
                    />
                    <ActiveBadge isActive={skill.is_active ?? false} />
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setEditingSkill(skill)}
                      className="w-8 h-8 rounded-md bg-[rgba(70,105,237,0.10)] flex items-center justify-center text-[#4669ed] hover:bg-[rgba(70,105,237,0.20)]"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setDeleteConfirmSkill(skill)}
                      className="w-8 h-8 rounded-md bg-[rgba(239,68,68,0.10)] flex items-center justify-center text-red-500 hover:bg-[rgba(239,68,68,0.20)]"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-6 border-t border-slate-200">
            <div className="text-sm text-slate-500">
              Showing {((page - 1) * PAGE_SIZE) + 1}-{Math.min(page * PAGE_SIZE, totalCount)} of {totalCount} skills
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="w-8 h-8 rounded-md border border-slate-200 flex items-center justify-center text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              {(() => {
                const maxVisible = 5;
                let startPage = Math.max(1, page - Math.floor(maxVisible / 2));
                const endPage = Math.min(totalPages, startPage + maxVisible - 1);
                startPage = Math.max(1, endPage - maxVisible + 1);
                return Array.from({ length: endPage - startPage + 1 }, (_, i) => {
                  const pageNum = startPage + i;
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setPage(pageNum)}
                      className={cn(
                        "w-8 h-8 rounded-md border flex items-center justify-center text-sm font-bold",
                        page === pageNum
                          ? "bg-[#4669ed] border-[#4669ed] text-white"
                          : "border-slate-200 text-slate-700 hover:bg-slate-50"
                      )}
                    >
                      {pageNum}
                    </button>
                  );
                });
              })()}
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="w-8 h-8 rounded-md border border-slate-200 flex items-center justify-center text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Add Skill Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="rounded-[24px]">
          <DialogHeader>
            <DialogTitle>Add New Skill</DialogTitle>
            <DialogDescription>
              Add a new skill to the suggested skills list
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="new-skill-name">Skill Name</Label>
              <Input
                id="new-skill-name"
                value={newSkillName}
                onChange={(e) => setNewSkillName(e.target.value)}
                placeholder="e.g., Machine Learning"
                className="rounded-[12px] mt-2"
              />
            </div>
            <div>
              <Label htmlFor="new-skill-category">Category</Label>
              <Select value={newSkillCategory} onValueChange={setNewSkillCategory}>
                <SelectTrigger id="new-skill-category" className="rounded-[12px] mt-2">
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  {SKILL_CATEGORIES.map((cat) => (
                    <SelectItem key={cat} value={cat}>
                      {cat}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="new-skill-active"
                checked={newSkillActive}
                onCheckedChange={setNewSkillActive}
              />
              <Label htmlFor="new-skill-active">Active</Label>
            </div>
          </div>
          <DialogFooter>
            <button
              onClick={() => setIsAddDialogOpen(false)}
              className="px-6 py-2.5 rounded-full border-2 border-slate-200 text-slate-700 font-medium text-sm hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              onClick={handleAddSkill}
              disabled={!newSkillName.trim()}
              className="px-6 py-2.5 rounded-full text-white font-medium text-sm disabled:opacity-50"
              style={{
                background: 'linear-gradient(106.66deg, rgba(70, 105, 237, 1) 0%, rgba(54, 84, 233, 1) 100%)',
              }}
            >
              Add Skill
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Skill Dialog */}
      <Dialog open={!!editingSkill} onOpenChange={(open) => !open && setEditingSkill(null)}>
        <DialogContent className="rounded-[24px]">
          <DialogHeader>
            <DialogTitle>Edit Skill</DialogTitle>
            <DialogDescription>
              Update the skill details
            </DialogDescription>
          </DialogHeader>
          {editingSkill && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="edit-skill-name">Skill Name</Label>
                <Input
                  id="edit-skill-name"
                  value={editingSkill.name}
                  onChange={(e) => setEditingSkill({ ...editingSkill, name: e.target.value })}
                  className="rounded-[12px] mt-2"
                />
              </div>
              <div>
                <Label htmlFor="edit-skill-category">Category</Label>
                <Select
                  value={editingSkill.category || 'none'}
                  onValueChange={(value) => setEditingSkill({ ...editingSkill, category: value === 'none' ? null : value })}
                >
                  <SelectTrigger id="edit-skill-category" className="rounded-[12px] mt-2">
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No category</SelectItem>
                    {SKILL_CATEGORIES.map((cat) => (
                      <SelectItem key={cat} value={cat}>
                        {cat}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="edit-skill-active"
                  checked={editingSkill.is_active ?? false}
                  onCheckedChange={(checked) => setEditingSkill({ ...editingSkill, is_active: checked })}
                />
                <Label htmlFor="edit-skill-active">Active</Label>
              </div>
            </div>
          )}
          <DialogFooter>
            <button
              onClick={() => setEditingSkill(null)}
              className="px-6 py-2.5 rounded-full border-2 border-slate-200 text-slate-700 font-medium text-sm hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              onClick={handleUpdateSkill}
              className="px-6 py-2.5 rounded-full text-white font-medium text-sm"
              style={{
                background: 'linear-gradient(106.66deg, rgba(70, 105, 237, 1) 0%, rgba(54, 84, 233, 1) 100%)',
              }}
            >
              Save Changes
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirmSkill} onOpenChange={(open) => !open && setDeleteConfirmSkill(null)}>
        <DialogContent className="rounded-[24px]">
          <DialogHeader>
            <DialogTitle>Delete Skill</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deleteConfirmSkill?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <button
              onClick={() => setDeleteConfirmSkill(null)}
              className="px-6 py-2.5 rounded-full border-2 border-slate-200 text-slate-700 font-medium text-sm hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              onClick={handleDeleteSkill}
              className="px-6 py-2.5 rounded-full bg-red-500 text-white font-medium text-sm hover:bg-red-600"
            >
              Delete
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
